/**
 * エラー表示改善（docs/plan/error-display.md）の共通例外クラスと分類ロジック。
 * 依存を持たない純粋なモジュール（Node 単体でもテストできる）。
 *
 * source: eccube / network / http / firestore / rtdb / auth / storage / functions / client / unknown
 */

const FIREBASE_PREFIXES = ["auth", "storage", "functions", "firestore", "database", "messaging"];

// 通信系のコード（Firebase / axios / fetch）
const NETWORK_CODES = [
  "unavailable",
  "deadline-exceeded",
  "network-request-failed",
  "network_error",
  "retry-limit-exceeded",
  "econnaborted",
  "etimedout",
  "err_network",
];

// 認証・権限系のコード
const AUTH_CODES = [
  "permission-denied",
  "permission_denied",
  "unauthenticated",
  "unauthorized",
  "requires-recent-login",
  "user-token-expired",
  "invalid-user-token",
  "user-disabled",
  "id-token-expired",
  "id-token-revoked",
];

// 入力・状態系のコード
const INPUT_CODES = [
  "invalid-argument",
  "failed-precondition",
  "not-found",
  "already-exists",
  "out-of-range",
  "object-not-found",
  "invalid-email",
  "wrong-password",
  "user-not-found",
  "weak-password",
  "email-already-in-use",
  "user-mismatch",
  "invalid-login-credentials",
  "invalid-credential",
  "account-exists-with-different-credential",
  "popup-blocked",
  "popup-closed-by-user",
  "cancelled-popup-request",
];

// サーバー内部系のコード
const SERVER_CODES = [
  "internal",
  "unknown",
  "data-loss",
  "unimplemented",
  "resource-exhausted",
  "server-file-wrong-size",
  "too-many-requests",
  "operation-not-supported-in-this-environment",
  "auth-domain-config-required",
  "operation-not-allowed",
  "unauthorized-domain",
];

// Firebase Auth のコード → ユーザー向け文言（src/js/common.js fbmessageToJp と同じ文言。null は「ダイアログを出さない」）
// 分類の既定文より優先する。行動文が要るものは \n で 2 行にする
const AUTH_CODE_MESSAGES = {
  "cancelled-popup-request": null,
  "popup-closed-by-user": null,
  "email-already-in-use": "このメールアドレスは使用されています",
  "invalid-email": "メールアドレスの形式が正しくありません",
  "user-disabled": "サービスの利用が停止されています",
  "user-not-found": "メールアドレスまたはパスワードが違います",
  "user-mismatch": "メールアドレスまたはパスワードが違います",
  "wrong-password": "メールアドレスまたはパスワードが違います",
  "invalid-login-credentials": "メールアドレスまたはパスワードが違います",
  "invalid-credential": "メールアドレスまたはパスワードが違います",
  "weak-password": "パスワードは6文字以上にしてください",
  "popup-blocked": "認証ポップアップがブロックされました。ポップアップブロックをご利用の場合は設定を解除してください",
  "operation-not-supported-in-this-environment": "現在この認証方法はご利用頂けません",
  "auth-domain-config-required": "現在この認証方法はご利用頂けません",
  "operation-not-allowed": "現在この認証方法はご利用頂けません",
  "unauthorized-domain": "現在この認証方法はご利用頂けません",
  "requires-recent-login": "認証の有効期限が切れています",
  "too-many-requests": "ログインの試行回数が多すぎます。\nしばらく時間をおいてお試しください。",
  "account-exists-with-different-credential": "このメールアドレスは別のログイン方法で登録されています",
};

// ログに出す前に伏せる秘匿値。axios はリクエスト本文を文字列化して error.config.data に載せるため、
// 生の例外をそのままログに渡すと Firebase の ID トークンが平文で出る（docs/plan/error-display.md 3.5）
const SECRET_PATTERNS = [
  // token=... / idToken: "..." のような key=value 形式
  /(\b(?:id_?token|token|password|passwd|secret|api[_-]?key|authorization)\b\s*[=:]\s*"?)[^&\s"',}]+/gi,
  // JWT そのもの（key が無い形で出た場合の保険）
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]+/g,
];

/**
 * 文字列から秘匿値を伏せる。ログ・GA4 へ出す前に必ず通す
 * @param {string} text
 */
export function redactSecrets(text) {
  if (typeof text !== "string" || !text) return text;
  let out = text;
  out = out.replace(SECRET_PATTERNS[0], (m, head) => head + "***");
  out = out.replace(SECRET_PATTERNS[1], "***");
  return out;
}

/**
 * 何を渡されても落ちない文字列化。Object.create(null) や toString が投げるオブジェクトが
 * throw されても、ここで受け止める（ErrorHandler の「絶対に投げない」約束を支える）
 * @param {*} v
 */
function safeString(v) {
  try {
    return String(v);
  } catch (e) {
    try {
      return Object.prototype.toString.call(v);
    } catch (ignored) {
      return "[文字列化できない値]";
    }
  }
}

/** 落ちない JSON 化。循環参照や toJSON の失敗を受け止める */
function safeJson(v) {
  try {
    return JSON.stringify(v);
  } catch (e) {
    return "[JSON 化できない値]";
  }
}

function stripQuery(url) {
  if (!url || typeof url !== "string") return "";
  const idx = url.indexOf("?");
  return idx === -1 ? url : url.slice(0, idx);
}

function normalizeCode(code) {
  if (!code || typeof code !== "string") return "";
  let c = code.toLowerCase();
  for (const p of FIREBASE_PREFIXES) {
    if (c.startsWith(p + "/")) {
      c = c.slice(p.length + 1);
      break;
    }
  }
  return c;
}

function sourceFromFirebaseCode(code) {
  if (!code || typeof code !== "string") return "";
  const idx = code.indexOf("/");
  if (idx === -1) return "firestore"; // Firestore の code は接頭辞なし（permission-denied 等）
  const prefix = code.slice(0, idx);
  if (prefix === "database") return "rtdb";
  if (FIREBASE_PREFIXES.includes(prefix)) return prefix;
  return "";
}

export class AppError extends Error {
  /**
   * @param {object} fields
   * @param {string} [fields.source]     失敗源
   * @param {string} [fields.endpoint]   API のパス（クエリは除く）
   * @param {number} [fields.httpStatus] HTTP ステータス
   * @param {string} [fields.apiStatus]  PHP API の status（"200" / "0"）
   * @param {object} [fields.errors]     PHP API の errors（コード → 文言）
   * @param {string} [fields.code]       Firebase / axios のエラーコード（接頭辞を除いた小文字）
   * @param {string} [fields.message]    開発者向けメッセージ
   * @param {*}      [fields.cause]      元の例外
   */
  constructor(fields = {}) {
    super(fields.message ? safeString(fields.message) : "AppError");
    this.name = "AppError";
    this.source = fields.source || "unknown";
    this.endpoint = stripQuery(fields.endpoint);
    this.httpStatus = fields.httpStatus || 0;
    this.apiStatus = fields.apiStatus === undefined || fields.apiStatus === null ? "" : String(fields.apiStatus);
    this.errors = fields.errors && typeof fields.errors === "object" ? fields.errors : null;
    this.code = normalizeCode(fields.code);
    this.cause = fields.cause === undefined ? null : fields.cause;
  }

  /**
   * 何が来ても AppError に正規化する。AppError ならそのまま返す。
   * @param {*} error
   * @param {object} [extra] 補う情報（source / endpoint など。既に値がある項目は上書きしない）
   */
  static from(error, extra = {}) {
    if (error instanceof AppError) {
      for (const key of Object.keys(extra)) {
        if (error[key] === undefined || error[key] === null || error[key] === "" || error[key] === 0) {
          error[key] = key === "endpoint" ? stripQuery(extra[key]) : extra[key];
        }
      }
      return error;
    }

    // APIConnect の旧仕様 reject(0) / reject() など、例外オブジェクトが無い
    if (error === undefined || error === null || error === 0 || error === "") {
      return new AppError({ source: extra.source || "unknown", message: "no error object", ...extra, cause: error });
    }

    // axios（isAxiosError）
    if (error.isAxiosError || (error.config && error.request !== undefined)) {
      const endpoint = extra.endpoint || (error.config && error.config.url) || "";
      if (error.response) {
        return new AppError({
          source: extra.source || "http",
          endpoint,
          httpStatus: error.response.status,
          code: error.code,
          message: error.message,
          cause: error,
        });
      }
      return new AppError({
        source: "network",
        endpoint,
        code: error.code || "network_error",
        message: error.message,
        cause: error,
      });
    }

    // Firebase（FirebaseError: code を持つ）
    if (typeof error.code === "string" && (error.name === "FirebaseError" || sourceFromFirebaseCode(error.code) || error.code.indexOf("-") !== -1)) {
      return new AppError({
        source: extra.source || sourceFromFirebaseCode(error.code) || "firestore",
        endpoint: extra.endpoint,
        code: error.code,
        message: error.message,
        cause: error,
      });
    }

    // fetch の通信失敗（TypeError: Failed to fetch / NetworkError / Load failed）
    if (error instanceof TypeError && /failed to fetch|networkerror|load failed/i.test(error.message || "")) {
      return new AppError({ source: "network", endpoint: extra.endpoint, code: "network_error", message: error.message, cause: error });
    }

    // Realtime Database の権限エラー（Error: PERMISSION_DENIED: ...）
    if (error instanceof Error && /^permission_denied/i.test(error.message || "")) {
      return new AppError({ source: extra.source || "rtdb", code: "permission_denied", message: error.message, cause: error });
    }

    if (error instanceof Error) {
      return new AppError({ source: extra.source || "client", endpoint: extra.endpoint, message: error.message, cause: error });
    }

    return new AppError({ source: extra.source || "client", endpoint: extra.endpoint, message: safeString(error), cause: error });
  }

  /**
   * PHP API（EC-CUBE）の応答が status "200" 以外だったときの AppError。
   * @param {object} response axios の response（data に {status, response, errors}）
   */
  static fromApiResponse(response) {
    const body = (response && response.data) || {};
    const errors = body.errors && typeof body.errors === "object" ? body.errors : null;
    const firstMessage = errors ? Object.values(errors).map(safeString).join(" / ") : "";
    return new AppError({
      source: "eccube",
      endpoint: response && response.config && response.config.url,
      httpStatus: response && response.status,
      apiStatus: body.status,
      errors,
      message: `API status=${body.status} ${firstMessage}`.trim(),
    });
  }

  /** PHP API の errors のキー一覧 */
  errorKeys() {
    return this.errors ? Object.keys(this.errors) : [];
  }

  /** ログ用の一行要約。トークン等は含まない */
  summary() {
    const parts = [this.source];
    if (this.endpoint) parts.push(this.endpoint);
    if (this.httpStatus) parts.push(`http=${this.httpStatus}`);
    if (this.apiStatus) parts.push(`api=${this.apiStatus}`);
    if (this.code) parts.push(`code=${this.code}`);
    if (this.errors) parts.push(`errors=${safeJson(this.errors)}`);
    if (this.message) parts.push(`msg=${this.message}`);
    return redactSecrets(parts.join(" | "));
  }

  /**
   * ログに出してよい形に落とした cause。
   * 生の例外オブジェクトはリクエスト本文（token を含む）を抱えているため、そのままログへ渡さない。
   * スタック文字列だけを返し、さらに伏せ字化を通す
   */
  causeForLog() {
    const c = this.cause;
    if (!c) return undefined;
    const text = c instanceof Error ? c.stack || c.message || String(c) : "";
    return text ? redactSecrets(text) : undefined;
  }
}

/**
 * 原因の分類（docs/plan/error-display.md 3.2）
 *   A 通信 / B 認証・権限 / C 入力・状態 / D サーバー内部 / E 不明
 * @param {AppError} appError
 * @returns {"A"|"B"|"C"|"D"|"E"}
 */
export function classifyError(appError) {
  const e = appError instanceof AppError ? appError : AppError.from(appError);
  const keys = e.errorKeys();
  const errorText = e.errors ? Object.values(e.errors).map(String).join(" ") : "";

  // A: 通信
  if (e.source === "network") return "A";
  if (typeof navigator !== "undefined" && navigator.onLine === false) return "A";
  if (NETWORK_CODES.includes(e.code)) return "A";

  // B: 認証・権限
  if (keys.includes("token") || /tokenが無効/.test(errorText)) return "B";
  if (AUTH_CODES.includes(e.code)) return "B";
  if (e.httpStatus === 401 || e.httpStatus === 403) return "B";

  // D: サーバー内部
  if (e.httpStatus >= 500) return "D";
  if (keys.some((k) => k === "system" || k.startsWith("ECCUBE."))) return "D";
  if (SERVER_CODES.includes(e.code)) return "D";

  // C: 入力・状態
  if (e.source === "eccube" && e.apiStatus !== "" && e.apiStatus !== "200") return "C";
  if (INPUT_CODES.includes(e.code)) return "C";
  if (e.httpStatus >= 400 && e.httpStatus < 500) return "C";

  return "E";
}

/** 分類ごとの表示文（docs/plan/error-display.md 3.3） */
export const ERROR_CLASS_MESSAGES = {
  A: { line1: "通信に失敗しました。", line2: "電波状況をご確認のうえ、もう一度お試しください。" },
  B: { line1: "ログインの有効期限が切れました。", line2: "左下メニューからログアウトし、再度ログインしてからお試しください。" },
  C: { line1: "入力内容または注文状態に問題があります。", line2: "内容をご確認のうえ、もう一度お試しください。解決しない場合はお問い合わせください。" },
  D: { line1: "サーバーで問題が発生しました。", line2: "しばらく時間をおいてお試しください。" },
  E: { line1: "エラーが発生しました。", line2: "もう一度お試しいただき、解決しない場合はお問い合わせください。" },
};

/**
 * Firebase Auth の既知コードに対する文言。該当しなければ undefined、
 * 「ダイアログを出さない」コード（ユーザーが途中でやめた等）なら null
 * @param {AppError} appError
 */
export function authMessageFor(appError) {
  if (!appError || appError.source !== "auth") return undefined;
  if (!Object.prototype.hasOwnProperty.call(AUTH_CODE_MESSAGES, appError.code)) return undefined;
  return AUTH_CODE_MESSAGES[appError.code];
}

/** ダイアログを出さない失敗か（ユーザーがポップアップを閉じた等） */
export function isDialogSuppressed(appError) {
  return authMessageFor(appError) === null;
}

/**
 * ユーザー向け表示文を組み立てる。優先順: message > Auth の文言表 > 分類の既定文
 * @param {string} cls        分類 1 文字
 * @param {string} fullCode   例: "E-076"
 * @param {string} [message]  分類の既定文の代わりに使う本文（行動文込み）。コード行は必ず付ける
 * @param {AppError} [appError] Auth の文言表を引くために使う
 */
export function buildUserMessage(cls, fullCode, message, appError) {
  const body = message || authMessageFor(appError) || `${ERROR_CLASS_MESSAGES[cls].line1}\n${ERROR_CLASS_MESSAGES[cls].line2}`;
  return `${body}\nエラーコード: ${fullCode}`;
}

/** 3 桁の番号に正規化（"76" / 76 → "076"） */
export function normalizeSiteNo(siteNo) {
  const s = String(siteNo === undefined || siteNo === null ? "" : siteNo).replace(/\D/g, "");
  return s ? s.padStart(3, "0").slice(-3) : "000";
}
